import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AppHeader } from '@/components/AppHeader';
import { enroll, unenroll } from '@/lib/api/enrollment';
import { followTournament, unfollowTournament } from '@/lib/api/follow';
import {
  subscribeToDirector,
  unsubscribeFromDirector,
} from '@/lib/api/subscriptions';
import { ApiError } from '@/lib/api/core';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import { viewerHighlight } from '@/lib/viewerRow';
import { hasCreatedProfile } from '@/lib/profileStatus';
import { useAuth } from '@/contexts/AuthContext';
import type {
  PlayerProfile,
  RequiredProfileField,
} from '@swiss-magic/shared-types';
import { getGameConfig } from '@swiss-magic/shared-types';
import { useTournamentPageData } from './tournament-public/useTournamentPageData';
import { TournamentDetailsGrid } from './tournament-public/TournamentDetailsGrid';
import { TournamentHostBlock } from './tournament-public/TournamentHostBlock';
import { UnregisterConfirmDialog } from './tournament-public/UnregisterConfirmDialog';

// Slugified tiebreak names live under tournament.standings.tiebreak.*
// (shared with StandingsPage). Falls through to the raw canonical name
// on a miss so unknown criteria still render.
function tiebreakLabel(criterion: string, t: TFunction): string {
  const slug = criterion.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  const key = `standings.tiebreak.${slug}`;
  const translated = t(key);
  return translated === key ? criterion : translated;
}

function statusLabel(status: string, t: TFunction): string {
  // i18next returns the key on miss; fall back to a Capitalized + space-
  // separated rendering of the raw enum so unknown statuses still read.
  const key = `public_page.status.${status}`;
  const translated = t(key);
  if (translated !== key) return translated;
  return status.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function requiredFieldLabel(field: RequiredProfileField, t: TFunction): string {
  const key = `public_page.required_profile_fields.${field}`;
  const translated = t(key);
  return translated === key ? field : translated;
}

export function TournamentPublicPage() {
  const { t } = useTranslation('tournament');
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const {
    tournament,
    playerData,
    directorProfile,
    orgProfile,
    profile,
    myEnrollment,
    setMyEnrollment,
    following,
    setFollowing,
    subscribed,
    setSubscribed,
    loading,
    notFound,
  } = useTournamentPageData(slug, authLoading, user);
  const hasProfile = hasCreatedProfile(profile);

  const [submittingEnrollment, setSubmittingEnrollment] = useState(false);
  const [unregisterDialogOpen, setUnregisterDialogOpen] = useState(false);
  const [submittingUnregister, setSubmittingUnregister] = useState(false);
  const [submittingFollow, setSubmittingFollow] = useState(false);
  const [submittingSubscription, setSubmittingSubscription] = useState(false);

  async function handleRegisterClick() {
    if (!tournament || !slug) return;
    const intent = `registerFor=${encodeURIComponent(slug)}`;
    if (!user) {
      navigate(`/login?${intent}`);
      return;
    }
    if (!hasProfile) {
      navigate(`/profile?${intent}`);
      return;
    }
    setSubmittingEnrollment(true);
    try {
      // Use the Player returned by enroll directly — a second getMyEnrollment
      // round-trip doubles the cold-start exposure on Fly. Player.status and
      // MyEnrollment.status share the same union.
      const player = await enroll(tournament.id);
      setMyEnrollment({
        playerId: player.id,
        status: player.status,
        rejectionReason: player.rejectionReason,
      });
      // Story 016 — registering supersedes following; the backend has
      // already cleared the follower row, mirror that locally so the
      // Follow/Following affordance disappears immediately.
      setFollowing(false);
    } catch (err) {
      if (err instanceof ApiError) showErrorToast(err.userMessage);
      else showErrorToast(t('public_page.toast.register_failed'));
    } finally {
      setSubmittingEnrollment(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p>{t('loading')}</p>
      </div>
    );
  }

  if (notFound || !tournament) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="mx-auto max-w-4xl px-6 py-8">
          <p data-testid="not-found-message" className="text-center text-muted-foreground">
            {t('public_page.not_found')}
          </p>
        </main>
      </div>
    );
  }

  const registrationOpen = tournament.status === 'registration_open';
  const showPending = myEnrollment?.status === 'none';
  // Story 100 — spot-secured registration flow.
  const showSecured = myEnrollment?.status === 'spot_secured';
  const showWaitlisted = myEnrollment?.status === 'waitlisted';
  const showRegistered = myEnrollment?.status === 'accepted';
  const showRejected = myEnrollment?.status === 'rejected';
  const showBlocked = myEnrollment?.status === 'blocked';
  const showWithdrawn = myEnrollment?.status === 'withdrawn';
  const showEliminated = myEnrollment?.status === 'eliminated';
  const missingRequiredFields = computeMissingRequiredFields(
    tournament.requiredProfileFields ?? [],
    profile,
  );
  const profileBlocksRegister =
    user !== null && hasProfile && missingRequiredFields.length > 0;

  const showRegisterButton =
    registrationOpen &&
    tournament.onlineRegistration &&
    !profileBlocksRegister &&
    (!user ||
      myEnrollment === null ||
      myEnrollment.status === 'rejected');

  const showUnregisterButton =
    registrationOpen &&
    myEnrollment !== null &&
    (myEnrollment.status === 'none' || myEnrollment.status === 'accepted');

  const followable =
    tournament.status !== 'completed' &&
    (user === null || myEnrollment === null);
  const showFollowButton = followable && (user === null || !following);
  const showUnfollowButton = followable && user !== null && following;

  const isOwnTournament = user !== null && user.id === tournament.organizerId;
  const subscribable = !isOwnTournament;
  const showSubscribeButton =
    subscribable && (user === null || !subscribed);
  const showUnsubscribeButton =
    subscribable && user !== null && subscribed;

  async function handleFollow() {
    if (!tournament || !slug) return;
    if (!user) {
      navigate(`/login?followFor=${encodeURIComponent(slug)}`);
      return;
    }
    setSubmittingFollow(true);
    try {
      await followTournament(tournament.id);
      setFollowing(true);
    } catch (err) {
      if (err instanceof ApiError) showErrorToast(err.userMessage);
      else showErrorToast(t('public_page.toast.follow_failed'));
    } finally {
      setSubmittingFollow(false);
    }
  }

  async function handleSubscribe() {
    if (!tournament || !slug) return;
    if (!user) {
      navigate(`/login?subscribeForOrganizerOf=${encodeURIComponent(slug)}`);
      return;
    }
    setSubmittingSubscription(true);
    try {
      await subscribeToDirector(tournament.organizerId);
      setSubscribed(true);
    } catch (err) {
      if (err instanceof ApiError) showErrorToast(err.userMessage);
      else showErrorToast(t('public_page.toast.subscribe_failed'));
    } finally {
      setSubmittingSubscription(false);
    }
  }

  async function handleUnsubscribe() {
    if (!tournament) return;
    setSubmittingSubscription(true);
    try {
      await unsubscribeFromDirector(tournament.organizerId);
      setSubscribed(false);
    } catch (err) {
      if (err instanceof ApiError) showErrorToast(err.userMessage);
      else showErrorToast(t('public_page.toast.unsubscribe_failed'));
    } finally {
      setSubmittingSubscription(false);
    }
  }

  async function handleUnfollow() {
    if (!tournament) return;
    setSubmittingFollow(true);
    try {
      await unfollowTournament(tournament.id);
      setFollowing(false);
    } catch (err) {
      if (err instanceof ApiError) showErrorToast(err.userMessage);
      else showErrorToast(t('public_page.toast.unfollow_failed'));
    } finally {
      setSubmittingFollow(false);
    }
  }

  async function handleConfirmUnregister() {
    if (!tournament) return;
    setSubmittingUnregister(true);
    try {
      await unenroll(tournament.id);
      setMyEnrollment(null);
      setUnregisterDialogOpen(false);
      showSuccessToast(t('public_page.toast.unregister_success', { name: tournament.name }));
    } catch (err) {
      if (err instanceof ApiError) showErrorToast(err.userMessage);
      else showErrorToast(t('public_page.toast.unregister_failed'));
    } finally {
      setSubmittingUnregister(false);
    }
  }

  return (
    <div data-testid="public-tournament-page" className="min-h-screen bg-background">
      <AppHeader />

      <main className="mx-auto max-w-4xl px-4 md:px-6 py-6">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <h2 data-testid="tournament-name" className="text-xl md:text-2xl font-semibold">
              {tournament.name}
            </h2>
            <Badge variant="secondary" data-testid="tournament-status-badge">
              {statusLabel(tournament.status, t)}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mb-3">{t('public_page.subtitle')}</p>

          {profileBlocksRegister && registrationOpen && (
            <div
              data-testid="profile-incomplete-notice"
              className="mb-4 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            >
              <p className="mb-2 font-medium">
                {t('public_page.profile_incomplete_title')}
              </p>
              <p className="mb-2">
                {t('public_page.profile_incomplete_missing_prefix')}
                {missingRequiredFields
                  .map((f) => requiredFieldLabel(f, t))
                  .join(', ')}
              </p>
              <Link
                to={`/profile?returnTo=${encodeURIComponent(`/t/${slug ?? ''}`)}`}
                className="inline-block underline underline-offset-2"
              >
                {t('public_page.profile_incomplete_link')}
              </Link>
            </div>
          )}

          {(showPending ||
            showRegistered ||
            showRejected ||
            showBlocked ||
            showWithdrawn ||
            showEliminated ||
            showRegisterButton ||
            showFollowButton ||
            showUnfollowButton) && (
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
              {showRegisterButton && (
                <Button
                  size="sm"
                  data-testid="public-register-button"
                  disabled={submittingEnrollment}
                  onClick={handleRegisterClick}
                  className="self-start"
                >
                  {submittingEnrollment
                    ? t('public_page.buttons.registering')
                    : t('public_page.buttons.register')}
                </Button>
              )}
              {showUnregisterButton && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="public-unregister-button"
                  onClick={() => setUnregisterDialogOpen(true)}
                  className="self-start"
                >
                  {t('public_page.buttons.unregister')}
                </Button>
              )}
              {showFollowButton && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="public-follow-button"
                  onClick={handleFollow}
                  disabled={submittingFollow}
                  className="self-start"
                >
                  {submittingFollow
                    ? t('public_page.buttons.following_in_flight')
                    : t('public_page.buttons.follow')}
                </Button>
              )}
              {showUnfollowButton && (
                <>
                  <Badge
                    data-testid="public-following-badge"
                    variant="secondary"
                    className="self-start text-sm py-1 px-3"
                  >
                    {t('public_page.badges.following')}
                  </Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="public-unfollow-button"
                    onClick={handleUnfollow}
                    disabled={submittingFollow}
                    className="self-start"
                  >
                    {submittingFollow
                      ? t('public_page.buttons.unfollowing')
                      : t('public_page.buttons.unfollow')}
                  </Button>
                </>
              )}
              {showPending && (
                <Badge
                  data-testid="public-enrollment-badge-pending"
                  variant="secondary"
                  className="self-start text-sm py-1 px-3"
                >
                  {t('public_page.badges.pending')}
                </Badge>
              )}
              {showSecured && (
                <Badge
                  data-testid="public-enrollment-badge-secured"
                  variant="default"
                  className="self-start text-sm py-1 px-3"
                >
                  {t('public_page.badges.spot_secured')}
                </Badge>
              )}
              {showWaitlisted && (
                <Badge
                  data-testid="public-enrollment-badge-waitlisted"
                  variant="secondary"
                  className="self-start text-sm py-1 px-3"
                >
                  {t('public_page.badges.waitlisted')}
                </Badge>
              )}
              {showRegistered && (
                <Badge
                  data-testid="public-enrollment-badge-registered"
                  variant="default"
                  className="self-start text-sm py-1 px-3"
                >
                  {t('public_page.badges.registered')}
                </Badge>
              )}
              {showRejected && (
                <Badge
                  data-testid="public-enrollment-badge-rejected"
                  variant="destructive"
                  className="self-start text-sm py-1 px-3"
                >
                  {t('public_page.badges.rejected')}
                </Badge>
              )}
              {showBlocked && (
                <Badge
                  data-testid="public-enrollment-badge-blocked"
                  variant="destructive"
                  className="self-start text-sm py-1 px-3"
                >
                  {t('public_page.badges.blocked')}
                </Badge>
              )}
              {showWithdrawn && (
                <Badge
                  data-testid="public-enrollment-badge-withdrawn"
                  variant="secondary"
                  className="self-start text-sm py-1 px-3"
                >
                  {t('public_page.badges.withdrawn')}
                </Badge>
              )}
              {showEliminated && (
                <Badge
                  data-testid="public-enrollment-badge-eliminated"
                  variant="secondary"
                  className="self-start text-sm py-1 px-3"
                >
                  {t('public_page.badges.eliminated')}
                </Badge>
              )}
            </div>
          )}

          {(showRejected || showBlocked) && myEnrollment?.rejectionReason && (
            <p
              data-testid="public-enrollment-rejection-reason"
              className="mb-4 text-sm text-muted-foreground"
            >
              {t('public_page.rejection_reason_prefix', { reason: myEnrollment.rejectionReason })}
            </p>
          )}

          {['registration_closed', 'in_progress', 'completed'].includes(tournament.status) && (
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <Link
                to={`/t/${slug}/standings`}
                data-testid="standings-link"
                className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                {t('common.section_labels.standings')}
              </Link>
              <Link
                to={`/t/${slug}/crosstable`}
                data-testid="crosstable-link"
                className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                {t('common.section_labels.crosstable')}
              </Link>
              <Link
                to={`/t/${slug}/pairings/1`}
                data-testid="pairings-link"
                className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                {t('common.section_labels.pairings')}
              </Link>
              <Link
                to={`/t/${slug}/starting-list`}
                data-testid="starting-list-link"
                className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                {t('common.section_labels.starting_list')}
              </Link>
            </div>
          )}
        </div>

        <div className="space-y-4">
            <TournamentDetailsGrid tournament={tournament} />

            {tournament.description && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('public_page.fields.description')}</p>
                <p data-testid="tournament-description">{tournament.description}</p>
              </div>
            )}

            <TournamentHostBlock
              orgProfile={orgProfile}
              directorProfile={directorProfile}
              subscribable={subscribable}
              showSubscribeButton={showSubscribeButton}
              showUnsubscribeButton={showUnsubscribeButton}
              submittingSubscription={submittingSubscription}
              onSubscribe={handleSubscribe}
              onUnsubscribe={handleUnsubscribe}
            />

            {tournament.tiebreakCriteria && tournament.tiebreakCriteria.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('public_page.fields.tiebreak_criteria')}</p>
                <ol data-testid="tournament-tiebreak-criteria" className="list-decimal list-inside">
                  {tournament.tiebreakCriteria.map((criterion) => (
                    <li key={criterion}>{tiebreakLabel(criterion, t)}</li>
                  ))}
                </ol>
              </div>
            )}

            {playerData && playerData.visibility !== 'hidden' && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('public_page.fields.players')}</p>
                <p data-testid="player-count">
                  {(playerData.count ?? 0) === 1
                    ? t('public_page.fields.player_count_singular')
                    : t('public_page.fields.player_count_plural', { count: playerData.count ?? 0 })}
                </p>
                {playerData.visibility === 'list' && playerData.players && playerData.players.length > 0 && (
                  <ul data-testid="public-player-list" className="mt-2 space-y-1">
                    {playerData.players.map((player) => {
                      const displayName = `${player.firstName} ${player.lastName}`.trim();
                      const location = [player.city, player.country].filter(Boolean).join('/');
                      const fullDisplay = location ? `${displayName} (${location})` : displayName;
                      const mine = viewerHighlight(player.profileUserId, user?.id);
                      return (
                        <li
                          key={player.id}
                          data-testid={`public-player-${fullDisplay}`}
                          data-viewer-row={mine.isMine ? 'true' : undefined}
                          className={`text-sm ${mine.isMine ? 'rounded px-2 py-1 -mx-2' : ''} ${mine.className}`}
                        >
                          {fullDisplay}
                          {getGameConfig(tournament.game).playerFields.rating && player.rating != null && <span className="ml-2 text-muted-foreground">({player.rating})</span>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
        </div>
      </main>

      <UnregisterConfirmDialog
        open={unregisterDialogOpen}
        submitting={submittingUnregister}
        tournamentName={tournament.name}
        onOpenChange={setUnregisterDialogOpen}
        onConfirm={handleConfirmUnregister}
      />
    </div>
  );
}

/**
 * Story 051 — given a tournament's required-fields list and the viewer's
 * profile, returns the codes the profile is missing. Returns the input
 * `requiredFields` unchanged when the profile is null (anonymous or no-
 * profile viewer) — the caller should treat that case via the existing
 * "no profile → /profile" redirect path, not via the in-page notice.
 */
function computeMissingRequiredFields(
  requiredFields: RequiredProfileField[],
  profile: PlayerProfile | null,
): RequiredProfileField[] {
  if (!profile || !hasCreatedProfile(profile) || requiredFields.length === 0) return [];
  const isBlankString = (v: unknown): boolean =>
    v == null || (typeof v === 'string' && v.trim() === '');
  return requiredFields.filter((field) => {
    switch (field) {
      case 'city': return isBlankString(profile.city);
      case 'country': return isBlankString(profile.country);
      case 'bio': return isBlankString(profile.bio);
      case 'avatar': return isBlankString(profile.avatarUrl);
      case 'chess_rating': return profile.chessRating == null;
      case 'chess_fide_id': return profile.chessFideId == null;
      case 'chess_club': return isBlankString(profile.chessClub);
      case 'backgammon_club': return isBlankString(profile.backgammonClub);
      case 'bmab_title': return isBlankString(profile.bmabTitle);
      default: return true;
    }
  });
}
